import { AfterViewInit, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import {
  AuthenticationResult,
  EventMessage,
  EventType,
  InteractionStatus,
} from '@azure/msal-browser';
import { PreloaderService } from '@core';
import { TranslateService } from '@ngx-translate/core';
import { NavigationService } from '@shared';
import { MandantConfigService } from '@strategies';
import { ToastrService } from 'ngx-toastr';
import { lastValueFrom, Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';

interface SwVersion {
  version: string;
  changelog: string;
}

@Component({
  selector: 'app-root',
  template: `
    <router-outlet></router-outlet>
    @if (useCookieConsent) {
      <app-cookie-consent></app-cookie-consent>
    }
  `,
  standalone: false,
})
export class AppComponent implements AfterViewInit, OnInit, OnDestroy {
  private preloader = inject(PreloaderService);
  private toastr = inject(ToastrService);
  private translateService = inject(TranslateService);
  private swUpdate = inject(SwUpdate);
  private mandantConfig = inject(MandantConfigService);
  private msalBroadcastService = inject(MsalBroadcastService);
  private msalAuthService = inject(MsalService);
  private navigationService = inject(NavigationService);

  // Debug-Schalter fuer die MSAL-Event-/Status-Logs (siehe ngOnInit).
  private static readonly DEBUG_AUTH_EVENTS = false;

  public useCookieConsent: boolean;

  private readonly destroying$ = new Subject<void>();

  ngAfterViewInit() {
    this.preloader.hide();
  }

  public ngOnInit() {
    // MSAL v5 setzt nach dem Login-Redirect keinen aktiven Account mehr (nur
    // Cache). Die Navigation (default-page prueft getActiveAccount()) laeuft
    // bei InteractionStatus.None los — also muss der aktive Account VORHER
    // gesetzt werden: LOGIN_SUCCESS/ACQUIRE_TOKEN_SUCCESS feuern vor
    // handleRedirectEnd. Ohne das: Endlosschleife b2c <-> callback.
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.LOGIN_SUCCESS ||
            msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
        ),
        takeUntil(this.destroying$)
      )
      .subscribe((msg: EventMessage) => {
        const result = msg.payload as AuthenticationResult;
        if (result?.account && !this.msalAuthService.instance.getActiveAccount()) {
          this.msalAuthService.instance.setActiveAccount(result.account);
        }
      });

    // MONITOR EVENTS:
    this.catchB2CErrors(true);
    // Zur Auth-Diagnose Flag auf true setzen:
    if (AppComponent.DEBUG_AUTH_EVENTS) {
      this.logB2CInteractionStatus();
      this.logB2CAuthenticationEvents();
    }

    this.showIosA2HsPopup();
    this.checkForUpdate();
    this.useCookieConsent = !!this.mandantConfig.mandant.cookieConsent;

    this.navigationService.initEventHandler();
  }

  ngOnDestroy(): void {
    console.log('destroying app');
    this.destroying$.next(undefined);
    this.destroying$.complete();
    console.log('destroyed app');
  }

  private showIosA2HsPopup() {
    const isIos = () => {
      const userAgent = window.navigator.userAgent.toLowerCase();
      return /iphone|ipad|ipod/.test(userAgent);
    };

    const isInStandaloneMode = () => window.matchMedia('(display-mode: standalone)').matches;

    if (isIos() && !isInStandaloneMode()) {
      this.toastr.show(this.translateService.instant('pwa.ios_install'));
    }
  }

  private checkForUpdate() {
    if (this.swUpdate.isEnabled) {
      this.swUpdate.versionUpdates
        .pipe(
          filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'),
          map(evt => ({
            type: 'UPDATE_AVAILABLE',
            current: evt.currentVersion,
            available: evt.latestVersion,
          }))
        )
        .subscribe(evt => {
          const oldAppData: SwVersion = <SwVersion>evt.current.appData;
          const newAppData: SwVersion = <SwVersion>evt.available.appData;
          let msg = '';
          this.translateService
            .get('pwa.update_available', {
              oldVersion: oldAppData.version,
              newVersion: newAppData.version,
              changelog: newAppData.changelog,
            })
            .subscribe((text: string) => {
              msg = text;
            });
          const updateApp: any = window.confirm(msg);
          if (updateApp) {
            window.location.reload();
          }
        });
    }
  }

  private logB2CInteractionStatus(): void {
    this.msalBroadcastService.inProgress$
      .pipe(takeUntil(this.destroying$))
      .subscribe((result: InteractionStatus) => {
        console.log('INTERACTION STATUS: ', result);
      });
  }

  private logB2CAuthenticationEvents(): void {
    this.msalBroadcastService.msalSubject$
      .pipe(takeUntil(this.destroying$))
      .subscribe((result: EventMessage) => {
        console.log('EVENTMESSAGE: ', result);
      });
  }

  private catchB2CErrors(silent: boolean = true): void {
    lastValueFrom(
      // don't use the .instance.handleRedirectPromise, it creates the bug described a few lines later.
      this.msalAuthService.handleRedirectObservable()
    )
      .then((_result: AuthenticationResult | null) => {
        // MSAL v5: Nach erfolgreichem Redirect liegt der Account im Cache,
        // aber es wird kein aktiver Account gesetzt. default-page prueft
        // getActiveAccount() -> ohne aktiven Account ging es zurueck zur
        // Login-Route und der SSO-Redirect lief in einer Endlosschleife.
        const instance = this.msalAuthService.instance;
        if (_result?.account) {
          instance.setActiveAccount(_result.account);
        } else if (!instance.getActiveAccount() && instance.getAllAccounts().length > 0) {
          instance.setActiveAccount(instance.getAllAccounts()[0]);
        }
      })
      .catch((_reason: Error) => {
        if (
          _reason.message.includes(
            'AADB2C90091: The user has cancelled entering self-asserted information.'
          )
        ) {
          if (!silent) {
            console.log('User cancelled reset-password');
          }
          // [TODO: KB] this is a workaround for a bug that prevents the user from accessing any
          // guarded route (e.g. /selfservice) after canceling the "reset password flow".
          this.msalAuthService.logoutRedirect();
        } else {
          // Auth-Fehler duerfen nie stumm verschluckt werden (Login-Loop-Debugging).
          console.error('[msal] handleRedirect fehlgeschlagen:', _reason);
        }
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        // msal-browser v5: LOGIN_FAILURE entfaellt, Login-Fehler kommen als
        // ACQUIRE_TOKEN_FAILURE.
        filter((msg: EventMessage) => msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE),
        takeUntil(this.destroying$)
      )
      .subscribe((_result: EventMessage) => {
        if (!silent) {
          console.log('!! LOGIN FAILED !!', _result);
        }
        // Add your auth error handling logic here
      });
  }
}
